import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  getPreview, getFullReport, submitEmail, generateReport,
  createCheckout, getPaymentStatus,
} from "@/lib/api";
import {
  Download, Mail, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, AlertOctagon, Lock, Sparkles, ShieldCheck, TrendingDown, TrendingUp, Phone, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import ScoreGauge from "@/components/ScoreGauge";
import CategoryBars from "@/components/CategoryBars";
import RevenueScenarioChart from "@/components/RevenueScenarioChart";
import Logo from "@/components/Logo";

const CATEGORY_LABELS = {
  business_profile: "Business Profile",
  lead_management: "Lead Management",
  sales: "Sales",
  operations: "Operations",
  marketing: "Marketing",
  administration: "Administration",
};

// Dev-only logger — silent in production builds.
const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

export default function Report() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdParam = searchParams.get("session_id");

  const [preview, setPreview] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [stage, setStage] = useState("loading");
  // stages: loading | email_gate | generating | preview | verifying_payment | unlocked | error

  const [email, setEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const reportRef = useRef(null);

  const loadPreview = useCallback(async () => {
    const data = await getPreview(id);
    setPreview(data);
    return data;
  }, [id]);

  const ensureReport = useCallback(async () => {
    setStage("generating");
    await generateReport(id);
    const data = await loadPreview();
    return data;
  }, [id, loadPreview]);

  const loadFullIfPaid = useCallback(async () => {
    try {
      const full = await getFullReport(id);
      setFullReport(full);
      setStage("unlocked");
    } catch (e) {
      // Expected 402 while unpaid — no action needed; log in dev only.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("loadFullIfPaid: report not unlocked yet", e?.response?.status);
      }
    }
  }, [id]);

  // Initial bootstrap
  useEffect(() => {
    (async () => {
      try {
        let data;
        try {
          data = await loadPreview();
        } catch (err) {
          if (err?.response?.status === 404) { setStage("error"); return; }
          throw err;
        }

        // Returning from Stripe checkout?
        if (sessionIdParam) {
          setStage("verifying_payment");
          pollPayment(sessionIdParam);
          return;
        }

        // If we have report + paid, show full
        if (data.paid && data.report_ready) {
          await loadFullIfPaid();
          return;
        }

        // No report yet → need email gate first, then generate
        if (!data.report_ready) {
          // If no contact info we ask for email first
          setStage("email_gate");
          return;
        }

        // Report ready, not paid → preview
        setStage("preview");
      } catch (e) {
        devLog(e);
        setStage("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pollPayment = async (sessionId, attempt = 0) => {
    const maxAttempts = 15;
    try {
      const status = await getPaymentStatus(sessionId);
      if (status.payment_status === "paid") {
        toast.success("Payment confirmed. Unlocking your full report…");
        setSearchParams({}, { replace: true });
        await loadFullIfPaid();
        return;
      }
      if (status.status === "expired") {
        toast.error("Checkout session expired. Please try again.");
        setSearchParams({}, { replace: true });
        const p = await loadPreview();
        setStage(p.report_ready ? "preview" : "email_gate");
        return;
      }
      if (attempt >= maxAttempts) {
        toast.error("Payment status check timed out. Please refresh in a minute.");
        setSearchParams({}, { replace: true });
        const p = await loadPreview();
        setStage(p.report_ready ? "preview" : "email_gate");
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
    } catch (e) {
      devLog("poll error", e);
      if (attempt < maxAttempts) {
        setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
      } else {
        toast.error("Could not verify payment. Please refresh.");
        setStage("error");
      }
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setEmailSubmitting(true);
    try {
      await submitEmail(id, { email, contact_name: contactName });
      await ensureReport();
      setStage("preview");
    } catch (err) {
      devLog(err);
      toast.error("Could not save your email. Please try again.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleUnlock = async () => {
    setCheckoutLoading(true);
    try {
      const data = await createCheckout(id, window.location.origin);
      if (data.already_paid) {
        await loadFullIfPaid();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      devLog(e);
      toast.error("Could not initiate checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!reportRef.current) return;
    toast.info("Preparing your PDF…");
    const opt = {
      margin: [24, 0, 24, 0],
      filename: `ScaleIQ_${preview?.business_snapshot?.name || "Audit"}.pdf`.replace(/[^a-z0-9_\-.]/gi, "_"),
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: 1200 },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
      pagebreak: {
        mode: ["css", "legacy"],
        before: ".pdf-page:not(:first-of-type)",
        avoid: [".pdf-no-break", ".pdf-keep-together"],
      },
    };
    html2pdf().set(opt).from(reportRef.current).save();
  };

  // ──────────────── render ────────────────
  if (stage === "loading") return <FullScreenStatus msg="Loading your audit…" />;
  if (stage === "verifying_payment") return <FullScreenStatus msg="Verifying your payment…" />;
  if (stage === "error") return <FullScreenError />;

  if (stage === "email_gate") return (
    <EmailGate
      preview={preview}
      email={email} setEmail={setEmail}
      contactName={contactName} setContactName={setContactName}
      onSubmit={handleEmailSubmit} submitting={emailSubmitting}
    />
  );

  if (stage === "generating") return <GeneratingScreen />;

  if (stage === "preview") return (
    <PreviewView
      preview={preview}
      onUnlock={handleUnlock}
      checkoutLoading={checkoutLoading}
    />
  );

  // unlocked → full report
  return (
    <FullReportView
      assessment={fullReport}
      onDownloadPdf={handleDownloadPdf}
      reportRef={reportRef}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Preview View — score + top 3 strengths + top 3 opportunities + $95 paywall
// ─────────────────────────────────────────────────────────────────────────────
function PreviewView({ preview, onUnlock, checkoutLoading }) {
  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      <header className="border-b border-gold/10 sticky top-0 bg-navy-900/95 backdrop-blur z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo size="sm" /></Link>
          <div className="hidden md:flex items-center gap-2 text-sm text-ink-muted">
            <Sparkles className="h-4 w-4 text-gold" /> Diagnostic preview
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Header block */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <div className="overline text-gold mb-3">Your ScaleIQ Diagnostic</div>
            <h1 className="font-display text-4xl md:text-5xl font-light" data-testid="business-name">{preview.business_snapshot.name}</h1>
            <div className="text-ink-muted text-sm mt-2">{preview.business_snapshot.industry} · {preview.business_snapshot.annual_revenue} · {preview.business_snapshot.employee_count} employees</div>
          </div>
          <div className="text-right">
            <div className="overline text-ink-muted">Audit Completed</div>
            <div className="text-sm">{new Date(preview.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>

        {/* Score + Categories */}
        <section className="border border-gold/15 bg-navy-800 p-8 md:p-12 mb-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5 flex justify-center">
              <ScoreGauge score={preview.overall_score} />
            </div>
            <div className="md:col-span-7">
              <div className="overline text-gold mb-2 flex items-center gap-2"><Sparkles className="h-3 w-3" /> Your AI Readiness Score</div>
              <h2 className="font-display text-3xl md:text-4xl font-light mb-4">{scoreVerdict(preview.overall_score)}</h2>
              <p className="text-ink-muted leading-relaxed mb-8">
                A 0–100 measure of operational maturity across lead management, sales, operations, marketing, and administration. Higher scores mean AI will produce ROI. Lower scores mean fix process first — or AI just amplifies chaos.
              </p>
              <div>
                <div className="overline text-ink-muted mb-3">Category Breakdown</div>
                <CategoryBars scores={preview.category_scores} labels={CATEGORY_LABELS} />
              </div>
            </div>
          </div>
        </section>

        {/* Strengths */}
        <section className="mb-10" data-testid="strengths-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-gold mb-2">What you're doing well</div>
              <h2 className="font-display text-3xl font-light">Top 3 strengths · <span className="text-gold italic">ready to amplify with AI</span></h2>
            </div>
            <div className="text-sm text-ink-muted hidden md:block">Identified from your audit responses</div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(preview.top_strengths || []).map((s, i) => (
              <div key={s.title || `strength-${i}`} className="border border-ok/30 bg-navy-800 p-6" data-testid={`strength-${i}`}>
                <div className="flex items-center gap-2 mb-3 text-ok">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
                  <div className="overline">Strength · 0{i + 1}</div>
                </div>
                <h3 className="font-display text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Opportunities */}
        <section className="mb-12" data-testid="opportunities-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-gold mb-2">The real bottlenecks</div>
              <h2 className="font-display text-3xl font-light">Top 5 process problems · <span className="text-gold italic">what's costing you money</span></h2>
            </div>
            <div className="text-sm text-ink-muted hidden md:block">Ranked by potential impact</div>
          </div>
          <div className="space-y-4">
            {(preview.top_opportunities || []).map((o, i) => (
              <div key={o.title || `opportunity-${i}`} className="border border-gold/20 bg-navy-800 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`opportunity-${i}`}>
                <div className="flex-1">
                  <div className="overline text-gold mb-1">{o.category} · Problem {i + 1}</div>
                  <h3 className="font-display text-xl mb-2">{o.title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{o.preview_summary}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-ink-muted shrink-0">
                  <Lock className="h-3 w-3" /> Diagnosis in the paid report
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Revenue Impact Banner */}
        <RevenueImpactBanner preview={preview} />

        {/* Revenue Trajectory Chart */}
        <RevenueTrajectorySection preview={preview} />

        {/* Recommended Tech Stack Preview (locked) */}
        <TechStackPreview preview={preview} />

        {/* Paywall */}
        <section className="relative border border-gold bg-navy-800 p-8 md:p-12 overflow-hidden" data-testid="paywall-section">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
            backgroundImage: "url('/assets/scaleiq-logo.png')",
            backgroundSize: "240px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right -40px center",
          }} />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="overline text-gold mb-3 flex items-center gap-2"><Lock className="h-3 w-3" /> Unlock the full diagnostic</div>
              <h2 className="font-display text-3xl md:text-4xl font-light mb-4">
                Capture the <span className="text-gold tabular-nums">${(preview.revenue_scenarios?.with_playbook_plus_call_12mo_uplift_usd || 0).toLocaleString()}</span> in projected 12-month upside
              </h2>
              <p className="text-ink-muted leading-relaxed mb-6">
                The full executive diagnostic for {preview.business_snapshot.name}: process maturity by department, prioritized bottlenecks, and specific AI + automation moves tied to dollar impact — including where NOT to use AI. One payment, instant unlock, downloadable PDF.
              </p>
              <ul className="space-y-2 text-sm text-ink-muted mb-2">
                {[
                  "Executive summary: what's broken and what it's worth",
                  "Estimated monthly savings + revenue opportunity",
                  "Six-department process maturity analysis",
                  "AI Opportunity Scores per department",
                  "Prioritized bottlenecks with dollar impact",
                  "High-level technology stack recommendations",
                  "12-month revenue forecast (do-nothing vs strategy vs blueprint)",
                  "Downloadable PDF deliverable",
                ].map((item) => (
                  <li key={item} className="flex gap-3"><span className="text-gold">›</span><span>{item}</span></li>
                ))}
                <li className="flex gap-3 pt-2 text-ink">
                  <span className="text-gold">★</span>
                  <span><strong className="text-gold">Included:</strong> $200 credit toward your Executive Strategy Session if booked within 7 days of purchase</span>
                </li>
              </ul>
              <p className="text-xs text-ink-muted italic mt-4">
                This is a diagnostic — like an MRI. It tells you where the tear is. Implementation lives in the strategy sessions and blueprints that follow.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="border border-gold/30 bg-navy-900 p-8 text-center">
                <div className="overline text-ink-muted mb-2">Executive Diagnostic</div>
                <div className="font-display text-6xl text-gold mb-1 tabular-nums">${'\u200a'}{preview.price_usd?.toFixed?.(0) ?? "97"}</div>
                <div className="text-xs text-ink-muted mb-6">one-time · instant unlock · $200 credit toward next step</div>
                <button
                  onClick={onUnlock}
                  disabled={checkoutLoading}
                  data-testid="unlock-report-button"
                  className="w-full bg-gold text-navy-900 px-6 py-4 font-semibold hover:bg-gold-400 disabled:opacity-60 inline-flex items-center justify-center gap-3"
                >
                  {checkoutLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <>Unlock Full Report — ${preview.price_usd?.toFixed?.(0) ?? "97"}</>}
                </button>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-ink-muted">
                  <ShieldCheck className="h-3 w-3" /> Secure payment via Stripe
                </div>
                <p className="mt-3 text-[11px] text-ink-muted leading-relaxed">
                  By purchasing, you agree to our <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="text-center mt-10 text-xs text-ink-muted space-y-1">
          <div>ScaleIQ · Scale Smart. Grow Fast.</div>
          <div>Questions? <a href="mailto:kevin@tryscaleiq.com" className="text-gold hover:underline">kevin@tryscaleiq.com</a></div>
        </div>
      </main>
    </div>
  );
}

function scoreVerdict(s) {
  if (s >= 80) return "AI-ready. Amplify what's working.";
  if (s >= 65) return "Solid core. AI will produce ROI here.";
  if (s >= 45) return "Fix process first. Then AI will scale.";
  return "AI would amplify chaos today. Diagnose first.";
}

// ─────────────────────────────────────────────────────────────────────────────
// Preview helper sections — conversion drivers between opportunities & paywall
// ─────────────────────────────────────────────────────────────────────────────
const usd = (n) => `$${(n ?? 0).toLocaleString()}`;

function RevenueImpactBanner({ preview }) {
  const fi = preview.financial_impact || {};
  const rs = preview.revenue_scenarios || {};
  const monthlyLoss = rs.monthly_revenue_loss_usd;
  const annualLoss = rs.annual_revenue_loss_usd ?? (monthlyLoss ? monthlyLoss * 12 : null);
  // Only render when we have an authoritative loss number from the scenarios (avoids
  // semantically wrong "revenue loss" framing on legacy reports without scenarios).
  if (!monthlyLoss) return null;
  return (
    <section className="mb-12 relative border border-bad/40 bg-gradient-to-br from-navy-800 to-navy-900 p-8 md:p-10" data-testid="revenue-loss-banner">
      <div className="grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <div className="overline text-bad mb-3 flex items-center gap-2">
            <TrendingDown className="h-3.5 w-3.5" /> What it's costing you to wait
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mb-3">
            You're leaving roughly <span className="text-bad tabular-nums">{usd(monthlyLoss)}</span> on the table <span className="text-ink-muted">every month.</span>
          </h2>
          <p className="text-ink-muted leading-relaxed">
            Based on your audit responses, the combined cost of operational leak + missed revenue opportunities is approximately <span className="text-ink font-semibold">{usd(annualLoss)} per year</span>. Confidence: {fi.confidence || "Moderate"}.
          </p>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-px bg-gold/15 border border-gold/15">
          <div className="bg-navy-900 p-5">
            <div className="overline text-ink-muted mb-1">Monthly savings missed</div>
            <div className="font-display text-2xl text-ok tabular-nums">{usd(fi.monthly_savings_usd)}</div>
          </div>
          <div className="bg-navy-900 p-5">
            <div className="overline text-ink-muted mb-1">Monthly revenue missed</div>
            <div className="font-display text-2xl text-ok tabular-nums">{usd(fi.monthly_revenue_gain_usd)}</div>
          </div>
          <div className="bg-navy-900 p-5 col-span-2 border-t border-gold/15">
            <div className="overline text-gold mb-1">12-month total impact at stake</div>
            <div className="font-display text-3xl text-gold tabular-nums">{usd(fi.annual_impact_usd ?? annualLoss)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RevenueTrajectorySection({ preview }) {
  const rs = preview.revenue_scenarios;
  if (!rs?.monthly_trajectory) return null;
  const playbookUplift = rs.with_playbook_12mo_uplift_usd || 0;
  const callUplift = rs.with_playbook_plus_call_12mo_uplift_usd || 0;
  const accelerator = playbookUplift ? Math.max(1, callUplift / playbookUplift) : 0;
  return (
    <section className="mb-12 border border-gold/15 bg-navy-800 p-8 md:p-10" data-testid="revenue-trajectory-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
        <div>
          <div className="overline text-gold mb-2 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" /> Projected 12-month upside</div>
          <h2 className="font-display text-3xl font-light">Three trajectories. <span className="text-gold italic">One decision today.</span></h2>
        </div>
        <div className="text-xs text-ink-muted max-w-md md:text-right">
          {rs.narrative || "Based on your audit, here's what the next 12 months look like under each path."}
        </div>
      </div>
      <RevenueScenarioChart trajectory={rs.monthly_trajectory} />
      <div className="grid md:grid-cols-3 gap-px bg-gold/15 border border-gold/15 mt-8">
        <div className="bg-navy-900 p-6" data-testid="scenario-status-quo">
          <div className="overline text-bad mb-2 flex items-center gap-2"><TrendingDown className="h-3 w-3" /> Do nothing</div>
          <div className="font-display text-3xl text-bad tabular-nums mb-1">{usd(-(rs.annual_revenue_loss_usd || 0))}</div>
          <div className="text-xs text-ink-muted">Continued monthly leak. Same problems compound.</div>
        </div>
        <div className="bg-navy-900 p-6" data-testid="scenario-playbook">
          <div className="overline text-ink-muted mb-2">Self-implement playbook</div>
          <div className="font-display text-3xl text-ink tabular-nums mb-1">+{usd(playbookUplift)}</div>
          <div className="text-xs text-ink-muted">12-month additional upside from executing the report yourself.</div>
        </div>
        <div className="bg-navy-900 p-6 relative" data-testid="scenario-call">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gold" />
          <div className="overline text-gold mb-2 flex items-center gap-2"><Phone className="h-3 w-3" /> Diagnostic + Strategy Session</div>
          <div className="font-display text-3xl text-gold tabular-nums mb-1">+{usd(callUplift)}</div>
          <div className="text-xs text-ink-muted">{accelerator > 1 ? `${accelerator.toFixed(1)}× faster execution. ` : ""}$200 credit inside — $599 net if booked in 7 days.</div>
        </div>
      </div>
    </section>
  );
}

function TechStackPreview({ preview }) {
  const stack = preview.tech_stack_preview || [];
  if (!stack.length) return null;
  return (
    <section className="mb-12" data-testid="tech-stack-preview">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline text-gold mb-2">Recommended tech stack</div>
          <h2 className="font-display text-3xl font-light">5 platforms hand-picked for your operation</h2>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-ink-muted">
          <Lock className="h-3 w-3" /> Specific picks revealed in the full report
        </div>
      </div>
      <div className="grid md:grid-cols-5 gap-3">
        {stack.map((s, i) => (
          <div key={s.key} className="relative border border-gold/15 bg-navy-800 p-5 overflow-hidden" data-testid={`tech-preview-${s.key}`}>
            <div className="overline text-gold mb-2">{s.category}</div>
            <div className="font-display text-base text-ink mb-3 leading-tight">{s.teaser}</div>
            <div className="border border-dashed border-gold/30 bg-navy-900 px-3 py-2 flex items-center justify-center gap-2 text-xs text-ink-muted">
              <Lock className="h-3 w-3 text-gold" /> Pick locked
            </div>
            <div className="mt-2 text-[10px] text-ink-muted/70 uppercase tracking-widest">Direction + example product</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Deprecated placeholder — quick wins removed from $97 diagnostic scope.
// eslint-disable-next-line no-unused-vars
function ImplementationPreview() {
  return null;
}

function LadderStep({ level, title, price, body, accent, done, highlight }) {
  const border = done ? "border-ok/40" : highlight ? "border-gold" : "border-gold/15";
  const overlineTone = done ? "text-ok" : highlight ? "text-gold" : "text-ink-muted";
  return (
    <div className={`border ${border} bg-navy-900 pdf-no-break p-6 grid md:grid-cols-12 gap-4 items-start relative`}>
      {highlight && <div className="absolute top-0 left-0 right-0 h-0.5 bg-gold" />}
      <div className="md:col-span-3">
        <div className={`overline ${overlineTone} mb-1`}>{level}</div>
        <div className="font-display text-2xl pdf-text">{title}</div>
      </div>
      <div className="md:col-span-7 text-sm text-ink-muted pdf-muted leading-relaxed">
        {body}
        {accent && <div className="mt-2 text-gold text-xs uppercase tracking-widest">{accent}</div>}
      </div>
      <div className="md:col-span-2 text-right">
        <div className={`font-display text-3xl tabular-nums ${highlight ? "text-gold" : "pdf-text"}`}>{price}</div>
        {done && <div className="text-[10px] text-ok uppercase tracking-widest mt-1">Complete</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Email gate
// ─────────────────────────────────────────────────────────────────────────────
function EmailGate({ preview, email, setEmail, contactName, setContactName, onSubmit, submitting }) {
  return (
    <div className="min-h-screen bg-navy-900 text-ink flex items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full">
        <div className="mb-8"><Logo size="md" /></div>
        <div className="overline text-gold mb-4">Final step</div>
        <h1 className="font-display text-4xl md:text-5xl font-light mb-4">Your diagnostic is scored. <span className="text-gold italic">Where should we send it?</span></h1>
        <p className="text-ink-muted mb-8">Your preliminary AI Readiness Score is <span className="text-gold font-semibold tabular-nums">{preview?.overall_score}/100</span>. Enter your email and we'll surface your strengths, top bottlenecks, and where AI actually fits — in under 90 seconds.</p>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="email-gate-form">
          <div>
            <label className="overline text-ink-muted">Your name</label>
            <input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="input-contact-name" placeholder="Jane Smith" className="w-full mt-2 bg-navy-800 border border-gold/15 px-4 py-3 text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="overline text-ink-muted">Email *</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="input-email" placeholder="you@company.com" className="w-full mt-2 bg-navy-800 border border-gold/15 px-4 py-3 text-ink placeholder:text-ink-muted/60 focus:outline-none focus:border-gold" />
          </div>
          <button disabled={submitting} data-testid="submit-email-button" className="w-full bg-gold text-navy-900 px-6 py-4 font-semibold hover:bg-gold-400 disabled:opacity-60 inline-flex items-center justify-center gap-3">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Diagnosing…</> : <><Mail className="h-4 w-4" /> See My Diagnostic</>}
          </button>
          <p className="text-xs text-ink-muted">By submitting this form, you agree to our <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>. Used only to deliver your audit. Questions? <a href="mailto:kevin@tryscaleiq.com" className="text-gold hover:underline">kevin@tryscaleiq.com</a></p>
        </form>
      </div>
    </div>
  );
}

const GeneratingScreen = () => {
  const [step, setStep] = useState(0);
  const stages = [
    "Consulting engine · reading your operational profile…",
    "Diagnosing lead-management and speed-to-lead process…",
    "Diagnosing sales execution and follow-up maturity…",
    "Diagnosing operations, dispatch, and administrative flow…",
    "Modeling revenue leak and 12-month forecast scenarios…",
    "Prescribing where AI belongs in your business (and where not)…",
  ];
  useEffect(() => {
    const t = setInterval(() => setStep((s) => Math.min(stages.length - 1, s + 1)), 2200);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen bg-navy-900 text-ink flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full text-center">
        <img
          src="/assets/scaleiq-logo.png"
          alt="ScaleIQ"
          draggable="false"
          className="h-48 md:h-64 lg:h-72 w-auto mx-auto mb-10 select-none drop-shadow-[0_0_60px_rgba(212,175,55,0.15)]"
          data-testid="generating-logo"
        />
        <div className="overline text-gold mb-4 flex items-center justify-center gap-3">
          <span className="h-px w-10 bg-gold" />
          ScaleIQ Proprietary Consulting Engine
          <span className="h-px w-10 bg-gold" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-light mb-10">Running your diagnostic…</h1>
        <div className="space-y-3 text-left max-w-md mx-auto">
          {stages.map((s, i) => (
            <div key={s} className={`flex items-center gap-3 text-sm transition-opacity duration-500 ${i <= step ? "opacity-100" : "opacity-25"}`}>
              {i < step ? <CheckCircle2 className="h-4 w-4 text-ok shrink-0" /> : i === step ? <Loader2 className="h-4 w-4 text-gold animate-spin shrink-0" /> : <span className="h-4 w-4 border border-ink-muted/30 shrink-0" />}
              <span className={i <= step ? "text-ink" : "text-ink-muted"}>{s}</span>
            </div>
          ))}
        </div>
        <div className="mt-10 text-xs text-ink-muted">This usually takes 30–90 seconds. Please don't close this window.</div>
      </div>
    </div>
  );
};

const FullScreenStatus = ({ msg }) => (
  <div className="min-h-screen bg-navy-900 text-ink flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="h-8 w-8 text-gold mx-auto mb-4 animate-spin" />
      <p className="text-ink-muted">{msg}</p>
    </div>
  </div>
);

const FullScreenError = () => (
  <div className="min-h-screen bg-navy-900 text-ink flex items-center justify-center">
    <div className="text-center max-w-md">
      <h1 className="font-display text-3xl mb-3">Something went wrong</h1>
      <p className="text-ink-muted mb-6">We couldn't load this audit. Please return home and try again.</p>
      <Link to="/" className="inline-flex items-center gap-2 text-gold border border-gold px-5 py-2.5"><ArrowLeft className="h-4 w-4" /> Back home</Link>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Full Report — same premium 5-page deliverable, only visible after payment
// ─────────────────────────────────────────────────────────────────────────────
function FullReportView({ assessment, onDownloadPdf, reportRef }) {
  const report = assessment.report;
  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      <header className="no-print border-b border-gold/10 sticky top-0 bg-navy-900/95 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo size="sm" /></Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-xs text-ok"><CheckCircle2 className="h-4 w-4" /> Unlocked</div>
            <button onClick={onDownloadPdf} data-testid="download-pdf-button" className="inline-flex items-center gap-2 bg-gold text-navy-900 px-5 py-2.5 font-semibold hover:bg-gold-400 transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>
        </div>
      </header>

      {/* Executive Strategy Session upsell (not part of PDF) */}
      <div className="no-print max-w-5xl mx-auto px-6 pt-8" data-testid="strategy-call-cta">
        <div className="border border-gold bg-navy-800 p-6 md:p-7">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 border border-gold flex items-center justify-center shrink-0">
                <Calendar className="h-5 w-5 text-gold" />
              </div>
              <div>
                <div className="overline text-gold mb-1">Ready for the "how"? · $200 credit inside</div>
                <h3 className="font-display text-xl md:text-2xl">Strategy Session + Transformation Blueprint</h3>
                <p className="text-sm text-ink-muted mt-1 max-w-xl">
                  Live 60-min working session with our team + your complete implementation playbook (vendors, stack, automation map, AI prompts, SOPs, ROI model). <span className="text-ink">Starting at $799</span> — apply your <span className="text-gold">$200 credit</span> from this purchase if you book within 7 days.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="text-right">
                <div className="font-display text-3xl text-gold tabular-nums">$599<span className="text-base text-ink-muted line-through ml-2">$799</span></div>
                <div className="text-[10px] text-ink-muted uppercase tracking-widest">with 7-day credit</div>
              </div>
              <a
                href={`mailto:kevin@tryscaleiq.com?subject=Strategy%20Session%20%2B%20Blueprint%20%2B%20%24200%20credit%20—%20${encodeURIComponent(assessment.business_snapshot.name)}&body=Hi%20Kevin%2C%0A%0AI%20just%20unlocked%20my%20ScaleIQ%20Executive%20Diagnostic%20for%20${encodeURIComponent(assessment.business_snapshot.name)}%20and%20would%20like%20to%20book%20the%20Strategy%20Session%20%2B%20Transformation%20Blueprint%20within%207%20days%20to%20apply%20my%20%24200%20credit%20(%24599%20net%20on%20the%20%24799%20starting%20price).%0A%0AMy%20availability%3A%0A%0AAssessment%20ID%3A%20${assessment.id}%0A%0AThanks%21`}
                data-testid="schedule-call-button"
                className="inline-flex items-center gap-2 bg-gold text-navy-900 px-6 py-3 font-semibold hover:bg-gold-400 transition-colors"
              >
                <Phone className="h-4 w-4" /> Book Now
              </a>
            </div>
          </div>
        </div>
      </div>

      <div ref={reportRef} className="max-w-5xl mx-auto px-6 py-12 space-y-12" data-testid="report-container">
        {/* Page 1 */}
        <ReportPage label="Page 01 · Executive Summary">
          <div className="flex justify-between items-start mb-10 pdf-no-break">
            <div>
              <div className="overline text-gold mb-3">Confidential Business Audit</div>
              <h1 className="font-display text-5xl font-light pdf-text">{assessment.business_snapshot.name}</h1>
              <div className="text-ink-muted pdf-muted text-sm mt-2">{assessment.business_snapshot.industry} · {assessment.business_snapshot.annual_revenue} · {assessment.business_snapshot.employee_count} employees</div>
            </div>
            <div className="text-right">
              <div className="overline text-ink-muted pdf-muted">Prepared</div>
              <div className="text-sm pdf-text">{new Date(assessment.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-8 items-center mb-10 pdf-no-break">
            <div className="md:col-span-5 flex justify-center">
              <ScoreGauge score={assessment.overall_score} />
            </div>
            <div className="md:col-span-7 space-y-5">
              <div>
                <div className="overline text-gold mb-1">Headline</div>
                <h2 className="font-display text-2xl font-medium pdf-text">{report.executive_summary.headline}</h2>
              </div>
              <p className="text-ink-muted pdf-muted leading-relaxed whitespace-pre-line">{report.executive_summary.narrative}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-px bg-gold/15 border border-gold/15 mb-10 pdf-no-break">
            <Metric label="Est. Monthly Savings" value={`$${report.executive_summary.estimated_monthly_savings_usd.toLocaleString()}`} tone="ok" testId="metric-savings" />
            <Metric label="Est. Monthly Revenue Gain" value={`$${report.executive_summary.estimated_monthly_revenue_gain_usd.toLocaleString()}`} tone="ok" testId="metric-gain" />
            <Metric label="Annual Impact" value={`$${report.executive_summary.annual_impact_usd.toLocaleString()}`} tone="gold" testId="metric-annual" />
          </div>

          <div className="pdf-no-break">
            <div className="overline text-gold mb-4">Category Breakdown</div>
            <CategoryBars scores={assessment.category_scores} labels={CATEGORY_LABELS} />
          </div>

          <div className="mt-8 text-xs text-ink-muted pdf-muted flex justify-between">
            <span>Confidence: {report.executive_summary.confidence}</span>
            <span>Generated by the ScaleIQ Consulting Engine</span>
          </div>
        </ReportPage>

        {/* Page 2 */}
        <ReportPage label="Page 02 · Current-State Analysis">
          <h2 className="font-display text-4xl font-light mb-8 pdf-text">Current-State Analysis</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <AnalysisBlock title="Strengths" icon={CheckCircle2} tone="ok" items={report.current_state.strengths} />
            <AnalysisBlock title="Weaknesses" icon={AlertTriangle} tone="warn" items={report.current_state.weaknesses} />
            <AnalysisBlock title="Bottlenecks" icon={AlertOctagon} tone="bad" items={report.current_state.bottlenecks} />
          </div>

          {Array.isArray(report.process_maturity_by_department) && report.process_maturity_by_department.length > 0 && (
            <div>
              <div className="overline text-gold mb-4">Process Maturity by Department</div>
              <div className="space-y-3">
                {report.process_maturity_by_department.map((pm, i) => (
                  <div key={pm.department || `pm-${i}`} className="border border-gold/15 p-5 grid grid-cols-12 gap-4 items-center pdf-no-break" data-testid={`pm-${i}`}>
                    <div className="col-span-12 md:col-span-3 font-display text-lg pdf-text">{pm.department}</div>
                    <div className="col-span-6 md:col-span-3">
                      <div className="overline text-ink-muted pdf-muted mb-1 text-[10px]">Process Maturity</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-navy-700 relative overflow-hidden">
                          <div className={`h-full ${pm.process_maturity_score >= 75 ? "bg-ok" : pm.process_maturity_score >= 50 ? "bg-warn" : "bg-bad"}`} style={{ width: `${pm.process_maturity_score}%` }} />
                        </div>
                        <div className="text-sm tabular-nums pdf-text w-8 text-right">{pm.process_maturity_score}</div>
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <div className="overline text-gold mb-1 text-[10px]">AI Opportunity</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-navy-700 relative overflow-hidden">
                          <div className="h-full bg-gold" style={{ width: `${pm.ai_opportunity_score}%` }} />
                        </div>
                        <div className="text-sm tabular-nums pdf-text w-8 text-right">{pm.ai_opportunity_score}</div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-3 text-sm text-ink-muted pdf-muted leading-snug">{pm.summary}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ReportPage>

        {/* Page 3 — Bottlenecks (diagnostic only) */}
        <ReportPage label="Page 03 · The 5 Costliest Bottlenecks">
          <h2 className="font-display text-4xl font-light mb-3 pdf-text">The 5 Costliest Bottlenecks</h2>
          <p className="text-ink-muted pdf-muted mb-8 max-w-2xl">A diagnostic reading of where money is leaking today, in priority order. The <span className="text-gold">how</span> to fix each is intentionally reserved for your Executive Strategy Session and Transformation Blueprint.</p>
          <div className="space-y-5">
            {report.opportunities.map((op, idx) => (
              <div key={op.title || `opportunity-full-${idx}`} className="border border-gold/15 p-6 pdf-no-break" data-testid={`opportunity-full-${idx}`}>
                <div className="flex items-start justify-between mb-3 gap-4">
                  <div>
                    <div className="overline text-gold mb-1">{op.category} · {op.priority}</div>
                    <h3 className="font-display text-2xl pdf-text">{op.title}</h3>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="overline text-ink-muted pdf-muted">Priority</div>
                    <div className="text-sm pdf-text">{op.priority}</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div className="md:col-span-2">
                    <div className="overline text-ink-muted pdf-muted mb-2">Diagnosis</div>
                    <p className="text-ink-muted pdf-muted leading-relaxed">{op.problem_summary}</p>
                  </div>
                  <div>
                    <div className="overline text-gold mb-2">Estimated Impact</div>
                    <p className="text-gold leading-relaxed">{op.estimated_impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ReportPage>

        {/* Page 4 — High-level Tech */}
        <ReportPage label="Page 04 · High-Level Technology Direction">
          <h2 className="font-display text-4xl font-light mb-3 pdf-text">High-Level Technology Direction</h2>
          <p className="text-ink-muted pdf-muted mb-8 max-w-2xl">The categories of tools you'll want as your process matures. Product-specific selection, sequencing, and configuration live in the Transformation Blueprint.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {Object.entries(report.tech_stack || {}).map(([key, val]) => (
              <div key={key} className="border border-gold/15 p-6 pdf-no-break" data-testid={`tech-${key}`}>
                <div className="overline text-gold mb-2">{key.toUpperCase()}</div>
                <div className="text-sm text-ink pdf-text mb-3 leading-relaxed">{val.category_recommendation || val.why || ""}</div>
                <div className="text-xs text-ink-muted pdf-muted">
                  Example product: <span className="text-ink pdf-text">{val.example_product || val.primary || ""}</span>
                </div>
              </div>
            ))}
          </div>
        </ReportPage>

        {/* Page 5 — Beyond the Diagnostic */}
        <ReportPage label="Page 05 · Beyond the Diagnostic">
          <h2 className="font-display text-4xl font-light mb-3 pdf-text">You have the MRI. Here's the ladder.</h2>
          <p className="text-ink-muted pdf-muted mb-10 max-w-2xl">This diagnostic told you <span className="text-gold">what is costing you money</span>. Below is what comes next — each step deeper than the last.</p>
          <div className="space-y-4">
            <LadderStep
              level="You are here"
              title="Executive Diagnostic"
              price="$97"
              done
              body="Where money is leaking. What the bottlenecks are. Six-department process maturity + AI opportunity read."
            />
            <LadderStep
              level="Step 2"
              title="Strategy Session + Transformation Blueprint"
              price="from $799"
              highlight
              accent="$200 credit inside · $599 net if booked within 7 days"
              body="A live 60-minute working session to review the diagnostic, prioritize the bottlenecks, and watch live tool demos — combined with your complete implementation playbook: week-by-week sequence, vendors, software stack, automation map, AI prompts, SOPs, and ROI model."
            />
            <LadderStep
              level="Step 3"
              title="Implementation Partners"
              price="Referral"
              body="Once you have the Blueprint, we match you with vetted implementation partners that specialize in businesses like yours. You are the architect. They are the builders."
            />
          </div>

          <div className="mt-12 text-center text-xs text-ink-muted pdf-muted">
            — End of Diagnostic —<br />
            Prepared by the ScaleIQ Consulting Engine · Scale Smart. Grow Fast.<br />
            Ready for the next step? <a href="mailto:kevin@tryscaleiq.com" className="text-gold">kevin@tryscaleiq.com</a>
          </div>
        </ReportPage>
      </div>
    </div>
  );
}

const ReportPage = ({ label, children }) => (
  <section className="bg-navy-800 border border-gold/15 p-10 md:p-14 pdf-page" data-testid={`report-page-${label.split(' ')[1]}`}>
    <div className="overline text-ink-muted pdf-muted mb-8">{label}</div>
    {children}
  </section>
);

const Metric = ({ label, value, tone, testId }) => {
  const toneClass = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : "text-gold";
  return (
    <div className="bg-navy-900 pdf-no-break p-6" data-testid={testId}>
      <div className="overline text-ink-muted pdf-muted mb-2">{label}</div>
      <div className={`font-display text-3xl tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
};

const AnalysisBlock = ({ title, icon: Icon, tone, items }) => {
  const toneClass = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : "text-bad";
  return (
    <div className="border border-gold/15 p-6" data-testid={`analysis-${title.toLowerCase()}`}>
      <div className={`flex items-center gap-2 mb-4 ${toneClass}`}>
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <div className="overline">{title}</div>
      </div>
      <ul className="space-y-4">
        {items.map((it, i) => (
          <li key={it.title || `item-${i}`}>
            <div className="font-display text-base pdf-text mb-1">{it.title}</div>
            <div className="text-sm text-ink-muted pdf-muted leading-relaxed">{it.detail}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};
